import { Check, Quote } from "lucide-react";

// 4 genuinely distinct description layouts. The same admin text is transformed
// into a different visual structure per template. Shared by form preview + product page.
export const DESCRIPTION_TEMPLATES = [
  { key: "classic", name: "Classic Prose" },
  { key: "boxed", name: "Feature Checklist" },
  { key: "highlight", name: "Lead Highlight" },
  { key: "minimal", name: "Minimal" },
];

const toParagraphs = (t) => t.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
const toItems = (t) => {
  const byLine = t.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (byLine.length > 1) return byLine;
  return t.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
};

export function ProductDescription({ template = "classic", text = "", compact = false }) {
  if (!text || !String(text).trim()) return null;
  const body = compact ? "text-xs" : "text-sm sm:text-base";

  // BOXED -> feature checklist card
  if (template === "boxed") {
    const items = toItems(text);
    return (
      <div className="rounded-xl border border-border bg-secondary/40 p-5" data-testid="desc-boxed">
        <p className="overline mb-3 text-primary">Key Highlights</p>
        <ul className="grid gap-2">
          {items.map((it, i) => (
            <li key={i} className={`flex items-start gap-2 ${body} text-foreground/90`}>
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                <Check className="h-3 w-3" />
              </span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // HIGHLIGHT -> big lead line + supporting muted text in a gradient callout
  if (template === "highlight") {
    const items = toItems(text);
    const lead = items[0];
    const rest = items.slice(1).join(" ");
    return (
      <div className="relative overflow-hidden rounded-xl border-l-4 border-primary bg-gradient-to-r from-accent to-transparent p-5" data-testid="desc-highlight">
        <Quote className="absolute right-4 top-4 h-8 w-8 text-primary/20" />
        <p className={`${compact ? "text-sm" : "text-lg sm:text-xl"} font-semibold leading-snug text-accent-foreground`}>{lead}</p>
        {rest && <p className={`mt-2 ${body} leading-relaxed text-muted-foreground`}>{rest}</p>}
      </div>
    );
  }

  // MINIMAL -> plain light typography, no decoration
  if (template === "minimal") {
    return (
      <p className={`${compact ? "text-sm" : "text-lg"} whitespace-pre-line font-light leading-relaxed text-muted-foreground`} data-testid="desc-minimal">
        {text}
      </p>
    );
  }

  // CLASSIC (default) -> titled prose block with paragraphs
  const paras = toParagraphs(text);
  return (
    <div className="border-l-2 border-primary/40 pl-4" data-testid="desc-classic">
      <p className="overline mb-2 text-primary">Description</p>
      <div className="grid gap-2">
        {paras.map((p, i) => (
          <p key={i} className={`${body} leading-relaxed text-muted-foreground`}>{p}</p>
        ))}
      </div>
    </div>
  );
}
